// The WALL (2026-09-28): between the router and the screens. No session →
// the PIN screen (pages/Login) at whatever URL was asked for, so a deep
// link survives the login and the back button never meets a /login entry.
// A stored session being re-verified → the APP LOADER (components/AppLoader,
// the mark and the ring; the plain board's skeleton until 2026-10-15), so
// a returning reader sees the app loading, never a flash of the wall. A
// standing session → the app. Logout flips the gate back in place (no
// navigation).
//
// LINK LOGIN (user call 2026-09-28): a URL carrying `?code=NNNN` logs an
// UNAUTHENTICATED visitor in with that code — the link a brand's contact is
// sent. The param is read ONCE, on mount, and REMOVED from the address bar
// in the same commit (a `replace`, so it is in no history entry, no
// screenshot, no copied link, no referrer from here on); the code itself
// lives in this component's state until the PIN screen has spent it. A
// reader who already holds a session keeps it — a link never swaps
// someone's workspace under them — and only loses the param. A malformed
// value (not 4–10 digits) is dropped and the wall says so. The one cost a
// code-in-a-URL always has — the request line in whoever's logs served it
// — is accepted for a 4-digit demo code.
//
// THE WAIT IS USED (2026-10-01): while the gate is shut at "/" the home's
// chunk is warmed (navigation/preloadable), so when the PIN screen opens the
// gate inside its view transition the app arrives as the home, never as a
// frame of the loader. A deep link's own page still loads cold behind the
// fallback, as before.
//
// LINK LAB (2026-10-08, user call: "beside the code … the AI lab id in a
// param, so when they are logged in that lab response is opened"): a URL
// carrying `?lab=<entry id>` (navigation/labLink) is FORWARDED, once a
// session stands — from the link's code, a typed one or a verified stored
// one — to the SESSION brand's Lab page with the id kept:
// `/?code=0441&lab=archive-bring-back` → the wall → `/brands/tiffany/lab?
// lab=archive-bring-back`. The Lab page then fires the entry's question
// (pages/Lab). The id is not scrubbed — it is not a secret, and the
// answer's URL is the point. A standing session is still never swapped:
// a link whose id belongs to another brand's pack lands on the reader's
// own home, its Lab view (the page sends an id its pack does not know
// home — pages/Lab). While the
// gate is shut on such a URL the LAB chunk is warmed instead of the home's,
// since the Lab is where the gate will open.
//
// THE HOME BEHIND THE ANSWER (2026-10-14, user report: from a link's answer
// "back in the browser leads out of the web app" — the hop was a replace,
// so the answer was the tab's only entry): the link's own entry is turned
// into the HOME (a replace — the dead landing URL, code and id, is still
// in no history entry) and the answer is PUSHED over it, with the home's
// view set to the WEEKLY PERSPECTIVE (`homeView`, the app's session
// memory — the view the user asked a link's reader to come back to). So
// the browser's back and the page's own "Go back" both land on the three
// questions; the push is counted for the back control (navigation/useBack
// `pushOwn`), since it is made behind the fallback, before the router
// subscribes.
import { useEffect, useLayoutEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import AppLoader, { OPENING_WORKSPACE } from "components/AppLoader";
import { preloadHome, preloadLab } from "navigation/RouterConfig";
import { useAppData } from "contexts/AppDataContext";
import { labPath, labUrl, readLabId, stripLabId } from "navigation/labLink";
import { pushOwn } from "navigation/useBack";
import {
  SESSION_CHECKING,
  SESSION_IN,
  SESSION_OUT,
  useSession,
} from "contexts/SessionContext";
import Login from "pages/Login";

export const CODE_PARAM = "code";
const CODE_SHAPE = /^\d{4,10}$/;

// what a URL carried: null (no param), a code, or "" for a malformed one
export function readLinkCode(search) {
  const value = new URLSearchParams(search).get(CODE_PARAM);
  if (value == null) return null;
  return CODE_SHAPE.test(value.trim()) ? value.trim() : "";
}

export function stripLinkCode(search) {
  const params = new URLSearchParams(search);
  params.delete(CODE_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

export default function AuthGate({ children }) {
  const { status, session } = useSession();
  const history = useHistory();
  const location = useLocation();
  const { remember } = useAppData();
  // a link's lab id, forwarded to the session's Lab page the moment the
  // session stands (and only while the URL is not already that page)
  const labId = readLabId(location.search);
  const labHop =
    status === SESSION_IN &&
    labId &&
    session &&
    location.pathname !== labPath(session.slug)
      ? labUrl(session.slug, labId)
      : null;
  // read once — the URL is scrubbed below and a later render must not see
  // the param gone and forget the code before the wall has used it
  const [linkCode, setLinkCode] = useState(() => readLinkCode(location.search));

  // …and SPENT once: the gate outlives a logout, so a code still held here
  // would refill the wall and log the reader straight back into the same
  // workspace (bug 2026-09-28: "I get the previous code prepopulated
  // instead of logging into a different brand"). The moment a session
  // stands — from the link, a typed code or a verified stored one — the
  // link's code is forgotten, and a later wall starts empty.
  useEffect(() => {
    if (status === SESSION_IN && linkCode != null) setLinkCode(null);
  }, [status, linkCode]);

  useEffect(() => {
    if (labId) preloadLab().catch(() => {});
    else if (location.pathname === "/") preloadHome().catch(() => {});
    // mount only: the gate is shut (or verifying) on the URL the app opened on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (!labHop) return;
    // the home behind the answer (see the head): the link's entry becomes
    // "/" without the code or the id (any board scope kept), the Weekly
    // view remembered for it, and the answer goes on top
    remember("homeView", "weekly");
    history.replace({
      pathname: "/",
      search: stripLabId(stripLinkCode(history.location.search)),
    });
    pushOwn(history, labHop);
  }, [labHop, history, remember]);

  useLayoutEffect(() => {
    // a hop at mount strips the code itself, on its way to the home entry
    if (linkCode == null || labHop) return;
    history.replace({
      pathname: location.pathname,
      search: stripLinkCode(location.search),
      hash: location.hash,
      state: location.state,
    });
    // mount only: the code was read from the URL the app opened on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === SESSION_CHECKING) {
    return <AppLoader label={OPENING_WORKSPACE} />;
  }
  if (status === SESSION_OUT) return <Login linkCode={linkCode} />;
  // the hop's commit shows the loader, so the home never mounts for a
  // frame at `/?lab=…` on its way to the Lab
  if (labHop) return <AppLoader label={OPENING_WORKSPACE} />;
  return children;
}
